import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectNumberFormat } from 'loot-core/src/client/selectors';
import { integerToCurrency } from 'loot-core/src/shared/util';
import { type LocalPrefs } from 'loot-core/src/types/prefs';

import { useLocalPref } from '../../hooks/useLocalPref';

export type FormatType =
  | 'string'
  | 'number'
  | 'percentage'
  | 'financial'
  | 'financial-with-sign';

function format(
  value: unknown,
  type: FormatType = 'string',
  formatter?: Intl.NumberFormat,
): string {
  switch (type) {
    case 'string':
      const val = JSON.stringify(value);
      // eslint-disable-next-line rulesdir/typography
      if (val.charAt(0) === '"' && val.charAt(val.length - 1) === '"') {
        return val.slice(1, -1);
      }
      return val;
    case 'number':
      return '' + value;
    case 'percentage':
      return value + '%';
    case 'financial-with-sign':
      const formatted = format(value, 'financial', formatter);
      if (typeof value === 'number' && value >= 0) {
        return '+' + formatted;
      }
      return formatted;
    case 'financial':
      if (value == null || value === '' || value === 0) {
        return integerToCurrency(0, formatter);
      } else if (typeof value === 'string') {
        const parsed = parseFloat(value);
        value = isNaN(parsed) ? 0 : parsed;
      }

      if (typeof value !== 'number') {
        throw new Error(
          'Value is not a number (' + typeof value + '): ' + value,
        );
      }

      return integerToCurrency(value, formatter);
    default:
      throw new Error('Unknown format type: ' + type);
  }
}

export function useFormat() {
  const numberFormat = useSelector(selectNumberFormat);

  return useCallback(
    (value: unknown, type: FormatType = 'string') =>
      format(value, type, numberFormat.formatter),
    [numberFormat],
  );
}

// Additive helper: applies the user's negativeDisplay preference on top of
// an already-formatted string. In 'minus' mode (the default) this is a
// no-op and the output is byte-for-byte identical to the existing
// `format`/`useFormat` behavior. In 'parentheses' mode, negative values have
// their minus sign stripped and are wrapped in parentheses instead.
function applyNegativeDisplay(
  formatted: string,
  value: unknown,
  negativeDisplay: LocalPrefs['negativeDisplay'] = 'minus',
): string {
  if (negativeDisplay !== 'parentheses') {
    return formatted;
  }

  let numericValue: number;
  if (typeof value === 'number') {
    numericValue = value;
  } else if (typeof value === 'string') {
    numericValue = parseFloat(value);
  } else {
    return formatted;
  }

  if (isNaN(numericValue) || numericValue >= 0) {
    return formatted;
  }

  // Strip the first minus sign (handles both a leading '-' and locales that
  // place it elsewhere, e.g. '$-1.00') and wrap the remainder in parens.
  return `(${formatted.replace('-', '')})`;
}

// Additive hook: identical to `useFormat`, but the returned formatter also
// honors the `negativeDisplay` preference (minus vs. parentheses) for
// negative values. Existing callers of `useFormat` are unaffected.
export function useFormatWithNegativeDisplay() {
  const numberFormat = useSelector(selectNumberFormat);
  const [negativeDisplay] = useLocalPref('negativeDisplay');

  return useCallback(
    (value: unknown, type: FormatType = 'string') => {
      const formatted = format(value, type, numberFormat.formatter);
      // Only currency is subject to the preference. A 'string' cell whose
      // text merely starts with a number (e.g. '-5 items') must not be
      // reinterpreted as a negative amount and wrapped in parens.
      if (type !== 'financial' && type !== 'financial-with-sign') {
        return formatted;
      }
      return applyNegativeDisplay(formatted, value, negativeDisplay);
    },
    [numberFormat, negativeDisplay],
  );
}
